const PUBLIC_ASSET_PATH = /^\/(?:assets|Sound|data)\//;
// `/guilde/` contient aussi la route applicative publique
// `/guilde/tutoriel`. Seuls les chemins qui designent effectivement un
// fichier appartiennent donc au depot de medias.
const GUILD_PUBLIC_ASSET_PATH = /^\/guilde\/[^?#\s]+\.[A-Za-z0-9]{1,10}(?:[?#].*)?$/;

function isPublicAssetPath(value: string): boolean {
  return PUBLIC_ASSET_PATH.test(value) || GUILD_PUBLIC_ASSET_PATH.test(value);
}

// Vite remplace cette constante dans le build. `typeof` garde cependant ce
// module importable par les tests Node/tsx, ou la valeur n'existe pas.
const COMPILED_ASSET_BASE = (
  typeof __PUBLIC_ASSET_BASE_URL__ === 'string' ? __PUBLIC_ASSET_BASE_URL__ : ''
).replace(/\/+$/, '');

const COMPILED_ASSET_VERSIONS: Readonly<Record<string, string>> = (
  typeof __PUBLIC_ASSET_VERSIONS__ === 'object' && __PUBLIC_ASSET_VERSIONS__
    ? __PUBLIC_ASSET_VERSIONS__
    : {}
);

function publicAssetVersionKey(pathname: string): string {
  try {
    const rawPath = pathname.split('/').map((segment) => decodeURIComponent(segment)).join('/');
    return encodeURIComponent(rawPath);
  } catch {
    return encodeURIComponent(pathname);
  }
}

/** Ajoute l'OID sans perdre une query ou une ancre métier déjà présente. */
function versionPublicAssetPath(value: string): string {
  const hashIndex = value.indexOf('#');
  const hash = hashIndex >= 0 ? value.slice(hashIndex) : '';
  const sansHash = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const queryIndex = sansHash.indexOf('?');
  const pathname = queryIndex >= 0 ? sansHash.slice(0, queryIndex) : sansHash;
  const query = queryIndex >= 0 ? sansHash.slice(queryIndex + 1) : '';
  const oid = COMPILED_ASSET_VERSIONS[publicAssetVersionKey(pathname)];
  if (!oid) return value;
  const params = new URLSearchParams(query);
  params.set('guild_oid', oid);
  return `${pathname}?${params.toString()}${hash}`;
}

/** Les sauvegardes ne conservent jamais une empreinte de transport. */
function unversionPublicAssetPath(value: string): string {
  const hashIndex = value.indexOf('#');
  const hash = hashIndex >= 0 ? value.slice(hashIndex) : '';
  const sansHash = hashIndex >= 0 ? value.slice(0, hashIndex) : value;
  const queryIndex = sansHash.indexOf('?');
  if (queryIndex < 0) return value;
  const pathname = sansHash.slice(0, queryIndex);
  const params = new URLSearchParams(sansHash.slice(queryIndex + 1));
  params.delete('guild_oid');
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ''}${hash}`;
}

/** Ajoute l'origine distante uniquement au moment ou une ressource est lue. */
export function resolvePublicAssetUrl(value: unknown): string {
  const raw = typeof value === 'string' ? value : '';
  if (!raw) return raw;
  const canonical = isPublicAssetPath(raw) ? raw : canonicalPublicAssetPath(raw);
  if (!isPublicAssetPath(canonical)) return raw;
  const versioned = versionPublicAssetPath(canonical);
  return COMPILED_ASSET_BASE ? `${COMPILED_ASSET_BASE}${versioned}` : versioned;
}

/**
 * Retire l'origine du build avant persistence. Les sauvegardes conservent
 * ainsi `/assets/...` et survivent a un changement de domaine ou de CDN.
 */
export function canonicalPublicAssetPath(value: unknown): string {
  const raw = typeof value === 'string' ? value : '';
  if (!raw) return raw;
  if (isPublicAssetPath(raw)) return unversionPublicAssetPath(raw);
  try {
    const browserOrigin = typeof globalThis.location?.origin === 'string'
      ? globalThis.location.origin
      : '';
    const base = new URL(COMPILED_ASSET_BASE || browserOrigin);
    const candidate = new URL(raw);
    if (candidate.origin !== base.origin) return raw;
    const basePath = base.pathname.replace(/\/+$/, '');
    if (basePath && !candidate.pathname.startsWith(`${basePath}/`)) return raw;
    const canonicalPath = candidate.pathname.slice(basePath.length) || '/';
    if (!isPublicAssetPath(canonicalPath)) return raw;
    return unversionPublicAssetPath(`${canonicalPath}${candidate.search}${candidate.hash}`);
  } catch {
    return raw;
  }
}

function isTraversableRecord(value: unknown): value is Record<string, unknown> {
  if (!value || typeof value !== 'object') return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

// La conversion ne doit jamais toucher une description, un message de chat ou
// un autre texte métier simplement parce qu'il vaut `/assets/...`. Seuls les
// champs dont le nom exprime une ressource peuvent contenir un chemin média.
const PUBLIC_ASSET_FIELD = /^(?:img|image(?:Url|DataUrl)?|images|avatar(?:Url|DataUrl)?|portrait(?:Url)?|asset(?:Url|s)?|icon(?:Url)?|logo(?:Url)?|sprite(?:Url|s)?|effectSpriteUrl|texture(?:Url|s)?|background(?:Url)?|thumbnail(?:Url)?|cover(?:Url)?|poster(?:Url)?|model(?:Url)?|sheet(?:Url)?|audio(?:Url)?|sound(?:Url)?|music(?:Url)?|src|layers|frames)$/i;

function isPublicAssetField(key: string): boolean {
  return PUBLIC_ASSET_FIELD.test(key);
}

/** Transforme recursivement les champs médias d'un DTO deja detache. */
export function transformPublicAssetUrlsInPlace<T>(
  value: T,
  transform: (entry: unknown) => string,
  seen = new WeakSet<object>(),
  mediaField = false,
): T {
  if (typeof value === 'string') return (mediaField ? transform(value) : value) as T;
  if (!value || typeof value !== 'object' || seen.has(value)) return value;
  if (!Array.isArray(value) && !isTraversableRecord(value)) return value;

  seen.add(value);
  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      value[index] = transformPublicAssetUrlsInPlace(value[index], transform, seen, mediaField);
    }
    return value;
  }

  const record = value as Record<string, unknown>;
  for (const [key, entry] of Object.entries(record)) {
    record[key] = transformPublicAssetUrlsInPlace(entry, transform, seen, isPublicAssetField(key));
  }
  return value;
}

/** Hydrate en place un DTO recu du serveur. */
export function resolvePublicAssetUrls<T>(value: T): T {
  return transformPublicAssetUrlsInPlace(value, resolvePublicAssetUrl);
}

/** Canonicalise en place une copie destinee a la persistence. */
export function canonicalizePublicAssetUrls<T>(value: T): T {
  return transformPublicAssetUrlsInPlace(value, canonicalPublicAssetPath);
}

/**
 * Produit un payload Socket.io canonique sans modifier les objets reactifs ou
 * les monstres vivants qui ont servi a le construire.
 */
export function canonicalPublicAssetPayload<T>(
  value: T,
  mappedBySource = {
    normal: new WeakMap<object, unknown>(),
    media: new WeakMap<object, unknown>(),
  },
  mediaField = false,
): T {
  if (typeof value === 'string') {
    return (mediaField ? canonicalPublicAssetPath(value) : value) as T;
  }
  if (!value || typeof value !== 'object') return value;
  if (!Array.isArray(value) && !isTraversableRecord(value)) return value;
  const mapped = mediaField ? mappedBySource.media : mappedBySource.normal;
  if (mapped.has(value)) return mapped.get(value) as T;

  mapped.set(value, value);
  if (Array.isArray(value)) {
    let result: unknown[] = value;
    for (let index = 0; index < value.length; index += 1) {
      const mappedEntry = canonicalPublicAssetPayload(value[index], mappedBySource, mediaField);
      if (mappedEntry !== value[index]) {
        if (result === value) {
          result = value.slice();
          mapped.set(value, result);
        }
        result[index] = mappedEntry;
      }
    }
    return result as unknown as T;
  }

  const record = value as Record<string, unknown>;
  let result = record;
  for (const [key, entry] of Object.entries(record)) {
    const mappedEntry = canonicalPublicAssetPayload(entry, mappedBySource, isPublicAssetField(key));
    if (mappedEntry !== entry) {
      if (result === record) {
        result = { ...record };
        mapped.set(value, result);
      }
      result[key] = mappedEntry;
    }
  }
  return result as unknown as T;
}
